import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { AverageBalanceSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/AverageBalanceSection.tsx';
import { AdditionalInfoSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/AdditionalInfoSection.tsx';
import { BankIncomeSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/BankIncomeSection.tsx';
import { BudgetDebtsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/budgetDebts/BudgetDebtsSection.tsx';
import { BorrowerDetailsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/BorrowerDetailsSection.tsx';
import { CreditOperationsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/creditOperations/CreditOperationsSection.tsx';
import { CurrencyExchangesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/currencyExchanges/CurrencyExchangesSection.tsx';
import { CourtCasesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/courtCases/CourtCasesSection.tsx';
import { ExpensesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/expenses/ExpensesSection.tsx';
import { FinancialAssistanceSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialAssistance/FinancialAssistanceSection.tsx';
import { FinancialInvestmentsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/financialInvestments/FinancialInvestmentsSection.tsx';
import { IncomeBreakdownsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/incomeBreakdowns/IncomeBreakdownsSection.tsx';
import { LiabilitiesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/liabilities/LiabilitiesSection.tsx';
import { LegalEntityFinancialStatementSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/LegalEntityFinancialStatementSection.tsx';
import { LoansAndBondsSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/loansAndBonds/LoansAndBondsSection.tsx';
import { OffBalanceLiabilitiesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/offBalanceLiabilities/OffBalanceLiabilitiesSection.tsx';
import { ReceivablesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/receivables/ReceivablesSection.tsx';
import { ReportPeriodSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/ReportPeriodSection.tsx';
import { SoleTraderFinancialStatementSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/SoleTraderFinancialStatementSection.tsx';
import { FinancialConditionSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionSection.tsx';
import {
  BorrowerType,
  FinancialStatementForm,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import {
  formatCertificateDate,
  getFinancialConditionDefaultValues,
  getMissingFinancialConditionRequiredFieldNames,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type { TDocumentGenerationFormProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';
import { HistoricalPrefillProvider } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/HistoricalPrefillProvider.tsx';
import { useDocumentGenerationFormSubmission } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/hooks/useDocumentGenerationFormSubmission.ts';
import { useFinancialConditionCertificateLoading } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useFinancialConditionCertificateLoading.ts';
import { useFinancialConditionFormSave } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useFinancialConditionFormSave.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  selectDocumentGenerationBanks,
  selectDocumentGenerationBanksStatus,
  selectDocumentGenerationCurrencies,
  selectDocumentGenerationCurrenciesStatus,
} from '~/store/covenants/selectors.ts';
import {
  fetchDocumentGenerationBanks,
  fetchDocumentGenerationCurrencies,
} from '~/store/covenants/thunks/dictionaryThunks.ts';
import { FetchStatus } from '~/store/covenants/types.ts';

export const FinancialConditionDocumentGenerationForm: React.FC<TDocumentGenerationFormProps> = ({
  disabled,
  formId,
  onSaveFinancialCondition,
  onLoadFinancialCondition,
  onDirtyChange,
  onBorrowerTypeChange,
  historicalBody,
  isHistoricalSource,
  onHistoricalPrefillPendingFieldsChange,
  onSaveAttemptChange,
  onValidationIssuesChange,
}) => {
  const dispatch = useAppDispatch();
  const banks = useSelector(selectDocumentGenerationBanks);
  const banksStatus = useSelector(selectDocumentGenerationBanksStatus);
  const currencies = useSelector(selectDocumentGenerationCurrencies);
  const currenciesStatus = useSelector(selectDocumentGenerationCurrenciesStatus);
  const hasRequestedDictionariesRef = useRef(false);
  const form = useForm<TFinancialConditionFormValues>({
    defaultValues: getFinancialConditionDefaultValues(),
  });
  const formValues = useWatch({
    control: form.control,
  }) as TFinancialConditionFormValues;
  const borrowerTypeOption = form.watch('borrowerType');
  const financialStatementFormOption = form.watch('financialStatementForm');
  const reportPeriodOption = form.watch('reportPeriod');
  const reportDate = form.watch('reportDate');
  const borrowerType = borrowerTypeOption?.value as BorrowerType | undefined;
  const financialStatementForm = financialStatementFormOption?.value as
    FinancialStatementForm | undefined;
  const isSoleTrader = borrowerType === BorrowerType.SoleTrader;
  const isLegalEntity = borrowerType === BorrowerType.LegalEntity;
  const isDictionariesReady =
    [FetchStatus.Success, FetchStatus.Error].includes(banksStatus) &&
    [FetchStatus.Success, FetchStatus.Error].includes(currenciesStatus);
  const { certificateVersionRef, historicalPrefilledFields, isLoadingCertificate } =
    useFinancialConditionCertificateLoading({
      form,
      banks,
      currencies,
      isDictionariesReady,
      isLegalEntity,
      financialStatementForm,
      financialStatementFormOption,
      historicalBody,
      isHistoricalSource,
      onLoadFinancialCondition,
    });
  const isFormDisabled = disabled || isLoadingCertificate;
  const reportPeriodLabel = reportPeriodOption?.label;
  const reportDateLabel = formatCertificateDate(reportDate);
  const missingRequiredFieldNames = useMemo(
    () => getMissingFinancialConditionRequiredFieldNames(formValues),
    [formValues]
  );

  useEffect(() => {
    onBorrowerTypeChange?.(borrowerType ?? null);
  }, [borrowerType, onBorrowerTypeChange]);

  const handleSave = useFinancialConditionFormSave({
    form,
    certificateVersionRef,
    onSaveFinancialCondition,
    onLoadFinancialCondition,
  });

  const { attemptSave } = useDocumentGenerationFormSubmission({
    form,
    onDirtyChange,
    onSaveAttemptChange,
    onValidationIssuesChange,
    onValidSubmit: handleSave,
    requiredFieldNames: missingRequiredFieldNames,
  });

  useEffect(() => {
    if (hasRequestedDictionariesRef.current) {
      return;
    }

    hasRequestedDictionariesRef.current = true;

    if (banksStatus !== FetchStatus.Success) {
      void dispatch(fetchDocumentGenerationBanks());
    }

    if (currenciesStatus !== FetchStatus.Success) {
      void dispatch(fetchDocumentGenerationCurrencies());
    }
  }, [banksStatus, currenciesStatus, dispatch]);

  return (
    <FormProvider {...form}>
      <HistoricalPrefillProvider
        fieldNames={historicalPrefilledFields}
        onPendingFieldsChange={onHistoricalPrefillPendingFieldsChange}
      >
        <Box
          component="form"
          id={formId}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            attemptSave();
          }}
          sx={documentGenerationFormStyles.form}
        >
          <FinancialConditionSection title="Загальні відомості">
            <BorrowerDetailsSection
              disabled={isFormDisabled}
              readOnly={Boolean(onLoadFinancialCondition)}
            />
            <ReportPeriodSection
              disabled={isFormDisabled}
              readOnly={Boolean(onLoadFinancialCondition)}
            />
          </FinancialConditionSection>
          {borrowerType && (
            <>
              <FinancialConditionSection
                title="1. Інформація про надходження грошових коштів та середньоденні залишки"
                description={`На рахунки Позичальника, відкриті в інших банках і небанківських фінансових установах${reportPeriodLabel ? ` за ${reportPeriodLabel}` : ''}, у тисячах одиниць.`}
                tooltip={FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES.bankIncome}
              >
                <BankIncomeSection disabled={isFormDisabled} />
                <AverageBalanceSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="3. Інформація про кредитні операції в інших банках та небанківських фінансових установах"
                description={
                  reportDateLabel
                    ? `Станом на ${reportDateLabel}.`
                    : 'Відомості про чинні кредитні операції Позичальника.'
                }
                tooltip={FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES.creditOperations}
              >
                <CreditOperationsSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="4. Інформація Позичальника про надані та отримані фінансові допомоги"
                description={
                  reportDateLabel
                    ? `Станом на ${reportDateLabel}.`
                    : 'Відомості про надані та отримані фінансові допомоги.'
                }
                tooltip={FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES.financialAssistance}
              >
                <FinancialAssistanceSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="5. Отримані позики та випущені облігаційні позики"
                description={
                  reportDateLabel
                    ? `Станом на ${reportDateLabel}.`
                    : reportPeriodLabel
                      ? `За ${reportPeriodLabel}.`
                      : 'Відомості про позики та облігаційні позики.'
                }
                tooltip="Якщо позик або облігаційних позик немає, скористайтеся відповідною позначкою у формі."
              >
                <LoansAndBondsSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="6. Позабалансові та інші зобов’язання Позичальника кредитного характеру"
                description={
                  reportDateLabel
                    ? `Станом на ${reportDateLabel}.`
                    : 'Відомості про договори, що передбачають позабалансові зобов’язання.'
                }
                tooltip={FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES.offBalanceLiabilities}
              >
                <OffBalanceLiabilitiesSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="7. Інформація про обсяги купівлі-продажу валюти Позичальником в інших банках"
                description={`${reportPeriodLabel ? `За ${reportPeriodLabel}` : 'За обраний звітний період'}, у тисячах одиниць.`}
                tooltip={FINANCIAL_CONDITION_TEMPLATE_FOOTNOTES.currencyExchanges}
              >
                <CurrencyExchangesSection disabled={isFormDisabled} />
              </FinancialConditionSection>
            </>
          )}
          {isSoleTrader && (
            <>
              <FinancialConditionSection
                title="8. Розшифровка загальної суми доходів"
                description="Питома вага відображається в загальній сумі доходу за відповідним рядком фінансової звітності."
                tooltip="Розшифровка може подаватися в іншому форматі за погодженням із Банком, але повинна містити всі зазначені параметри."
              >
                <IncomeBreakdownsSection disabled={isFormDisabled} />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="9. Відповідність фінансової звітності вимогам законодавства"
                description="Підтвердження відповідності фінансової звітності, наданої Банку, вимогам законодавства України."
              >
                <SoleTraderFinancialStatementSection />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="10. Заборгованість перед бюджетом, державними та цільовими фондами"
                description="Станом на дату подання інформації."
                tooltip="За наявності заборгованості зазначається повний перелік наявних заборгованостей і суми за ними."
              >
                <BudgetDebtsSection
                  disabled={isFormDisabled}
                  borrowerType={BorrowerType.SoleTrader}
                  sectionNumber={10}
                />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="11. Інформація про судові справи, в яких стороною є Позичальник"
                description="Станом на дату подання інформації."
                tooltip="Розкривається інформація про судові процеси, виконавчі провадження, справи про банкрутство, а також проблеми з органами влади, ДПС і правоохоронними органами."
              >
                <CourtCasesSection
                  disabled={isFormDisabled}
                  borrowerType={BorrowerType.SoleTrader}
                  sectionNumber={11}
                />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="12. Додаткова інформація"
                description="За потреби: зокрема інформація, що підтверджує виконання ковенантів або інших зобов’язань за Генеральною угодою."
              >
                <AdditionalInfoSection disabled={isFormDisabled} sectionNumber={12} />
              </FinancialConditionSection>
            </>
          )}
          {isLegalEntity && (
            <>
              {financialStatementForm && (
                <>
                  <FinancialConditionSection
                    title="8. Розшифровка рядків фінансової звітності щодо зобов’язань"
                    description="Окремо за річною та квартальною фінансовою звітністю."
                    tooltip="Велика форма: 8.1 — 1510, 1515, 1525, 1600, 1610, 1690; 8.2 — 1510. Мала / спрощена форма: 8.1 — 1595, 1600, 1690; 8.2 — 1595. Розшифровки можуть подаватися окремими таблицями в іншому погодженому з Банком форматі, але мають містити всі зазначені параметри."
                  >
                    <LiabilitiesSection disabled={isFormDisabled} />
                  </FinancialConditionSection>
                  <FinancialConditionSection
                    title="9. Розшифровка фінансових інвестицій"
                    description="Фінансові інвестиції за річною фінансовою звітністю."
                    tooltip="Велика форма: 1030, 1035, 1160. Мала / спрощена форма: 1030, 1160. Розкриваються фінансові інвестиції на строк понад один рік, а також усі інвестиції, які не можуть бути вільно реалізовані."
                  >
                    <FinancialInvestmentsSection disabled={isFormDisabled} />
                  </FinancialConditionSection>
                  <FinancialConditionSection
                    title="10. Розшифровка дебіторської заборгованості"
                    description="За річною фінансовою звітністю; розкриваються рядки, частка яких перевищує 5% валюти балансу."
                    tooltip="Велика форма: 1120, 1125, 1155, 1190, 1605. Мала / спрощена форма: 1090, 1125, 1155, 1190."
                  >
                    <ReceivablesSection disabled={isFormDisabled} />
                  </FinancialConditionSection>
                  <FinancialConditionSection
                    title="11. Розшифровка витрат за даними фінансової звітності"
                    description="Окремо за річною та квартальною звітністю Форм №2, №2-м і №2-мс."
                    tooltip="Велика форма: 11.1 — 2180, 2270, 2150, 2200, 2220, 2240, 2255; 11.2 — 2180, 2270. Мала / спрощена форма: 11.1 — 2180, 2270; 11.3 — фінансові витрати (2250) та амортизація (2515)."
                  >
                    <ExpensesSection disabled={isFormDisabled} />
                  </FinancialConditionSection>
                </>
              )}
              <FinancialConditionSection
                title="12. Відповідність фінансової звітності вимогам законодавства"
                description="Підтвердження відповідності фінансової звітності, наданої Банку, вимогам законодавства України."
              >
                <LegalEntityFinancialStatementSection />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="13. Заборгованість перед бюджетом, державними та цільовими фондами"
                description="Станом на дату подання інформації."
                tooltip="За наявності заборгованості зазначається повний перелік наявних заборгованостей і суми за ними."
              >
                <BudgetDebtsSection
                  disabled={isFormDisabled}
                  borrowerType={BorrowerType.LegalEntity}
                  sectionNumber={13}
                />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="14. Інформація про судові справи, в яких стороною є Позичальник"
                description="Станом на дату подання інформації."
                tooltip="Розкривається інформація про судові процеси, виконавчі провадження, справи про банкрутство, а також проблеми з органами влади, ДПС і правоохоронними органами."
              >
                <CourtCasesSection
                  disabled={isFormDisabled}
                  borrowerType={BorrowerType.LegalEntity}
                  sectionNumber={14}
                />
              </FinancialConditionSection>
              <FinancialConditionSection
                title="15. Додаткова інформація"
                description="За потреби: зокрема інформація, що підтверджує виконання ковенантів або інших зобов’язань за Генеральною угодою."
              >
                <AdditionalInfoSection disabled={isFormDisabled} sectionNumber={15} />
              </FinancialConditionSection>
            </>
          )}
        </Box>
      </HistoricalPrefillProvider>
    </FormProvider>
  );
};
